// TanStack Query hooks for the coach review surface (U6 of Plan 3).
//
// Three mutations: APPLY, DISMISS, SNOOZE. All invalidate the
// ['coach-latest'] cache on success so the REVIEW tab re-renders with
// fresh decision state. APPLY additionally invalidates Day-tab caches
// (R14: APPLY-d strength weights should appear on the Day view's next
// open without a manual reload).

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from './api';
import { OPEN_ADJUSTMENT_KEY } from './coach-adjust';
import {
  CoachActionResponseSchema,
  CoachWarningSchema,
  type CoachActionResponse,
  type CoachLatestSuggestion,
} from './types';

// Plan 2's POST /api/coach response shape — small enough to inline.
const CoachRunResponseSchema = z.object({
  coach_call_id: z.number(),
  retries: z.number(),
  parsed: z.unknown(),
  // Warn-tier rail findings on the accepted plan (always an array server-side).
  warnings: z.array(CoachWarningSchema).default([]),
});
type CoachRunResponse = z.infer<typeof CoachRunResponseSchema>;

type RunCoachVars = {
  weeks?: number;
  question?: string;
};

/** Fire an on-demand coach call (trigger=ad-hoc) and refresh REVIEW. */
export function useRunCoachMutation() {
  const qc = useQueryClient();
  return useMutation<CoachRunResponse, Error, RunCoachVars>({
    mutationFn: async (vars) => {
      const body: Record<string, unknown> = { trigger: 'ad-hoc' };
      if (vars.weeks !== undefined) body.weeks = vars.weeks;
      if (vars.question !== undefined) body.question = vars.question;
      return apiClient.post<CoachRunResponse>('/api/coach', body, {
        schema: CoachRunResponseSchema,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coach-latest'] });
    },
  });
}

type ApplyVars = { suggestion: CoachLatestSuggestion };
type DismissVars = { suggestion: CoachLatestSuggestion };
type SnoozeVars = {
  suggestion: CoachLatestSuggestion;
  snooze_until?: string;
};

// Returns the coach-latest refetch so a mutation can wait for it. The other
// invalidations fire and are not awaited: they refresh other surfaces, while
// coach-latest is what the button the operator just pressed is looking at.
function applyInvalidations(
  qc: ReturnType<typeof useQueryClient>,
  dimension: string,
  suggestion: unknown,
): Promise<void> {
  // Always invalidate the review surface so the decision badge flips.
  const decisions = qc.invalidateQueries({ queryKey: ['coach-latest'] });

  // R14: dimension-specific invalidations.
  if (dimension === 'activity') {
    // Day-tab workout query keys vary by date — fire a broad invalidation.
    qc.invalidateQueries({ queryKey: ['workouts'] });
  } else if (dimension === 'details') {
    // The `suggestion` payload comes from the server as `unknown` (the
    // shape varies per dimension and is validated server-side). Defend
    // against malformed/null payloads with a real runtime guard rather
    // than an unsafe cast — the consequence of a bad cast is a silent
    // failure to invalidate the right query.
    const kind =
      typeof suggestion === 'object' &&
      suggestion !== null &&
      'kind' in suggestion &&
      typeof (suggestion as { kind: unknown }).kind === 'string'
        ? (suggestion as { kind: string }).kind
        : null;
    if (kind === 'strength') {
      qc.invalidateQueries({ queryKey: ['exercise-weights'] });
    } else if (kind === 'run') {
      // Applying a coach run detail upserts daily_run_target — refresh the
      // run-target overlay so the Day/Plan run surfaces pick it up.
      qc.invalidateQueries({ queryKey: ['daily-run-target'] });
    }
  }
  else if (dimension === 'macros') {
    // Applying any macros suggestion rewrites the daily target singleton +
    // dated history (the whole-plan sum), so refresh both the targets read
    // and the per-date history overlay.
    qc.invalidateQueries({ queryKey: ['daily-targets'] });
    qc.invalidateQueries({ queryKey: ['daily-target-history'] });
  }
  // plan_timeline has no downstream cache to invalidate.
  return decisions;
}

export function useApplyMutation() {
  const qc = useQueryClient();
  return useMutation<CoachActionResponse, Error, ApplyVars>({
    mutationFn: async ({ suggestion }) => {
      return apiClient.post<CoachActionResponse>(
        `/api/coach/suggestions/${suggestion.coach_call_id}/${suggestion.id}/apply`,
        undefined,
        { schema: CoachActionResponseSchema },
      );
    },
    onSuccess: (_data, vars) =>
      applyInvalidations(qc, vars.suggestion.dimension, vars.suggestion.suggestion),
  });
}

// Apply ALL pending macros suggestions for a call in one click.
//
// Server semantics: applying ANY single per-meal macros suggestion already
// writes the WHOLE-plan summed daily target (+ dated history) — but only the
// clicked row flips to 'applied', leaving siblings 'pending' and the card
// showing "1 of 3 applied" even though the target is fully set. This applies
// each pending macros row sequentially so all three flip, matching reality.
// Sequential (not parallel) because each apply re-sums the same call's macros;
// concurrent CAS flips on the shared call are needless contention for 3 rows.
type ApplyAllMacrosVars = { suggestions: CoachLatestSuggestion[] };

export function useApplyAllMacrosMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, ApplyAllMacrosVars>({
    mutationFn: async ({ suggestions }) => {
      const pending = suggestions.filter(
        (s) => s.dimension === 'macros' && (s.status === 'pending' || s.was_snoozed),
      );
      for (const s of pending) {
        await apiClient.post<CoachActionResponse>(
          `/api/coach/suggestions/${s.coach_call_id}/${s.id}/apply`,
          undefined,
          { schema: CoachActionResponseSchema },
        );
      }
    },
    // onSettled (not onSuccess): the loop is non-atomic, so a mid-loop failure
    // still leaves earlier applies committed server-side. Reconcile the UI
    // regardless of outcome so it never shows stale state after a partial run.
    // Returned so the button stays APPLYING until the refreshed decisions are
    // on screen (see useBulkApplyMutation).
    onSettled: () => applyInvalidations(qc, 'macros', null),
  });
}

// Plan 4 follow-up (I3) — bulk apply all pending activity + details
// suggestions for a call. Macros + plan_timeline are excluded
// server-side. Invalidates both the REVIEW surface and the Day-tab
// workout/exercise caches since a bulk apply touches both.
const BulkApplyResponseSchema = z.object({
  applied: z.number(),
  skipped: z.number(),
  considered: z.number(),
  failures: z.array(z.object({ suggestion_id: z.string(), reason: z.string() })),
});
export type BulkApplyResponse = z.infer<typeof BulkApplyResponseSchema>;

export function useBulkApplyMutation() {
  const qc = useQueryClient();
  return useMutation<BulkApplyResponse, Error, { coachCallId: number }>({
    mutationFn: async ({ coachCallId }) =>
      apiClient.post<BulkApplyResponse>(
        '/api/coach/suggestions/bulk-apply',
        { coach_call_id: coachCallId },
        { schema: BulkApplyResponseSchema },
      ),
    // The refreshed decisions are awaited, not just requested. A 24-row apply
    // takes ~5 s server-side and the digest refetch another ~2 s; without the
    // wait the button returned to APPLY ALL while the count still read "27
    // pending", which looks exactly like a click that did nothing — and
    // invites a second one.
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['coach-horizon'] });
      qc.invalidateQueries({ queryKey: ['workouts'] });
      qc.invalidateQueries({ queryKey: ['exercise-weights'] });
      qc.invalidateQueries({ queryKey: ['daily-run-target'] });
      // APPLY ALL sets the accepted run plan → refresh the Day-view arc.
      qc.invalidateQueries({ queryKey: ['run-plan-active'] });
      // Applying an adjustment's sessions closes it as the open proposal.
      qc.invalidateQueries({ queryKey: OPEN_ADJUSTMENT_KEY });
      await qc.invalidateQueries({ queryKey: ['coach-latest'] });
    },
  });
}

export function useDismissMutation() {
  const qc = useQueryClient();
  return useMutation<CoachActionResponse, Error, DismissVars>({
    mutationFn: async ({ suggestion }) => {
      return apiClient.post<CoachActionResponse>(
        `/api/coach/suggestions/${suggestion.coach_call_id}/${suggestion.id}/dismiss`,
        undefined,
        { schema: CoachActionResponseSchema },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coach-latest'] });
    },
  });
}

export function useSnoozeMutation() {
  const qc = useQueryClient();
  return useMutation<CoachActionResponse, Error, SnoozeVars>({
    mutationFn: async ({ suggestion, snooze_until }) => {
      return apiClient.post<CoachActionResponse>(
        `/api/coach/suggestions/${suggestion.coach_call_id}/${suggestion.id}/snooze`,
        snooze_until ? { snooze_until } : {},
        { schema: CoachActionResponseSchema },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coach-latest'] });
    },
  });
}
